import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Space, Spin, Typography } from 'antd'
import { filter, get, isEmpty, isNil, map, some } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import {
  FormButtons,
  HistoryLink,
  ReduxMetadataTree,
  SearchHighlightedSpan,
  TypographyLinkWrapper,
} from '../../../common/components/sections'
import {
  StyledAlert,
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import {
  APP_ROUTES,
  EVENT_METADATA_TYPES,
  METADATA_TYPES,
  PERMISSIONS,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useDocumentTitle,
  useLatestEventByEntityTypesQuery,
  useUserHasPermissions,
} from '../../../common/hooks'

import { renderAwardEvent } from '../../helpers'
import {
  useAwardsQuery,
  useDeleteAwardMutation,
  useSaveAwardsMutation,
  useSetIsCurationEnabledAwardMutation,
} from '../../hooks'
import {
  addAwardNode,
  expandAwardMainTreeKeys,
  expandAwardSupportingTreeKeys,
  moveAwards,
  renameAwardNode,
  resetAwardTree,
  setDataForRenamingAward,
} from '../../redux/actions'

const AwardsPage = () => {
  const dispatch = useDispatch()
  useDocumentTitle('Awards')
  const [isMoveActive, setIsMoveActive] = useState(false)
  const [isReplaceActive, setIsReplaceActive] = useState(false)

  const eventEntityTypes = useMemo(() => [EVENT_METADATA_TYPES.AWARD], [])

  const {
    isLoading: isFetchLatestEventLoading,
    error: fetchLatestEventError,
    data: latestEvent,
    refetch: refetchLastEvent,
  } = useLatestEventByEntityTypesQuery(eventEntityTypes)

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    initialData,
  } = useAwardsQuery()

  const [saveAwards, { isLoading: isSaveLoading, error: saveError }] =
    useSaveAwardsMutation(refetchLastEvent)

  const [deleteAward] = useDeleteAwardMutation(refetchLastEvent)

  const [setIsCurationEnabledAward] =
    useSetIsCurationEnabledAwardMutation(refetchLastEvent)

  const {
    isDirty,
    data,
    dataForRenaming,
    mainTreeExpandedKeys,
    supportingTreeExpandedKeys,
  } = useSelector(state => state.awards.awards)

  const hasFullPermission = useUserHasPermissions([
    PERMISSIONS.METADATA_TREE.SAVE,
  ])

  const hasPermissionToViewDetails = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.GET,
  )

  const treeData = isDirty ? data : initialData

  const handleAdd = useCallback(
    node => dispatch(addAwardNode(node)),
    [dispatch],
  )

  const handleDelete = useCallback(({ id }) => deleteAward(id), [deleteAward])

  const handleSetIsCurationEnabled = useCallback(
    ({ id, isCurationEnabled }) =>
      setIsCurationEnabledAward(id, isCurationEnabled),
    [setIsCurationEnabledAward],
  )

  const handleRename = useCallback(
    ({ path, name }) => dispatch(renameAwardNode({ path, name })),
    [dispatch],
  )

  const handleSetDataForRenaming = useCallback(
    ({ id }) => dispatch(setDataForRenamingAward({ id })),
    [dispatch],
  )

  const handleMove = useCallback(
    (nodesToMove, destinationNodes) => {
      const [destinationNode] = destinationNodes
      const levelToMoveUnder = destinationNode.level - 1

      const parentSelectedNodes = filter(
        nodesToMove,
        ({ parentId }) => !some(nodesToMove, ({ id }) => id === parentId),
      )

      const restrictedToMoveNodes = filter(parentSelectedNodes, node => {
        if (levelToMoveUnder > node.level) {
          return !isEmpty(node.children)
        }

        if (levelToMoveUnder === node.level) {
          return !isEmpty(
            node.level === 1
              ? get(node, 'children[0].children')
              : node.children,
          )
        }

        return false
      })

      if (!isEmpty(restrictedToMoveNodes)) {
        showErrorNotification({
          title: 'Move Error',
          description: (
            <div>
              The following nodes can&apos;t be moved:
              {map(restrictedToMoveNodes, ({ name }) => (
                <Typography.Paragraph style={{ margin: 0 }} key={name}>
                  {name};
                </Typography.Paragraph>
              ))}
            </div>
          ),
        })
      } else {
        dispatch(
          moveAwards({
            nodesToMove: parentSelectedNodes,
            destinationNode,
          }),
        )
      }
    },
    [dispatch],
  )

  const handleResetTree = useCallback(
    () => dispatch(resetAwardTree()),
    [dispatch],
  )

  const handleSaveData = useCallback(() => saveAwards(data), [data, saveAwards])

  const nodeTitleContentRender = useCallback(
    (node, searchValue) => {
      const nodeContentTitle = (
        <Link
          to={`${APP_ROUTES.AWARDS.link}/${node.id}`}
          component={props => (
            <TypographyLinkWrapper
              {...props}
              target="_blank"
              strong={node.isErrorProne}
              disabled={isDirty}
            />
          )}
        >
          <SearchHighlightedSpan searchValue={searchValue}>
            {node.name}
          </SearchHighlightedSpan>
        </Link>
      )

      if (!isNil(node.replaceToken)) {
        return <StyledAlert message={nodeContentTitle} banner type="info" />
      }

      return nodeContentTitle
    },
    [isDirty],
  )

  useEffect(() => {
    if (saveError?.message || fetchError?.message || fetchLatestEventError) {
      showErrorNotification({
        title: 'Award error',
        description:
          saveError?.message || fetchError?.message || fetchLatestEventError,
      })
    }
  }, [fetchError, fetchLatestEventError, saveError])

  return isFetchLoading || isSaveLoading || isFetchLatestEventLoading ? (
    <Spin />
  ) : (
    <>
      <StyledPageHeader>
        <StyledPageTitle>Awards</StyledPageTitle>
        <StyledPageActions>
          <StyledVerticalSpace>
            <Space>
              <UpdateDataText
                updatedAt={latestEvent?.createdAt}
                updatedBy={latestEvent?.createdBy?.name}
              />

              <HistoryLink
                renderEvent={renderAwardEvent}
                filterEventTypes
                eventMetadataTypes={eventEntityTypes}
              />
            </Space>
            <FormButtons
              permissions={[PERMISSIONS.METADATA_TREE.SAVE]}
              isDisabled={isMoveActive || isReplaceActive ? true : !isDirty}
              onReset={handleResetTree}
              onSave={handleSaveData}
            />
          </StyledVerticalSpace>
        </StyledPageActions>
      </StyledPageHeader>
      <ReduxMetadataTree
        disableLink={!hasPermissionToViewDetails}
        mainTreeExpandedKeys={mainTreeExpandedKeys}
        supportingTreeExpandedKeys={supportingTreeExpandedKeys}
        expandMainTree={expandAwardMainTreeKeys}
        expandSupportingTree={expandAwardSupportingTreeKeys}
        metadataType={METADATA_TYPES.AWARDS}
        isDirty={isDirty}
        showSearch
        allowReplaceMany={false}
        treeData={treeData}
        editable={hasFullPermission}
        addNode={handleAdd}
        deleteNode={handleDelete}
        renameNode={handleRename}
        moveNodes={handleMove}
        setDataForRenaming={handleSetDataForRenaming}
        setIsCurationEnabled={handleSetIsCurationEnabled}
        dataForRenaming={dataForRenaming}
        nodeTitleContentRender={nodeTitleContentRender}
        isMoveActive={isMoveActive}
        setIsMoveActive={setIsMoveActive}
        isReplaceActive={isReplaceActive}
        setIsReplaceActive={setIsReplaceActive}
      />
    </>
  )
}

export default AwardsPage
